import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Eye, User, Shield, SkipForward } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration médias - Facilement modifiable
const MEDIA_CONFIG = {
  intro: {
    video: '/videos/intro-principale.mp4',
    poster: '/images/intro/poster.jpg',
    duration: 45000  // 45 secondes (durée de la vidéo)
  }
};

// Configuration phases - SIMPLIFIÉ
const PHASES = {
  VIDEO: 'video',      // Ta vidéo avec ta voix off
  PORTAL: 'portal',    // Choix Client/Visiteur
  COMPLETE: 'complete'
};

// Realistic Intro with Video
const RealisticIntro = ({ onComplete, startAtPortal = false }) => {
  // Phase: START (bouton clic) -> VIDEO -> PORTAL
  // Si startAtPortal est true, on démarre directement au portail (retour depuis login)
  const [phase, setPhase] = useState(startAtPortal ? PHASES.PORTAL : 'START');
  const [canSkip, setCanSkip] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminCredentials, setAdminCredentials] = useState({ email: '', password: '' });
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [currentSubtitle, setCurrentSubtitle] = useState('');
  const videoRef = useRef(null);

  // Enable skip after 1 second once video starts
  useEffect(() => {
    if (phase === PHASES.VIDEO) {
      const skipTimer = setTimeout(() => {
        setCanSkip(true);
      }, 1000);
      return () => clearTimeout(skipTimer);
    }
  }, [phase]);

  // Démarrer la vidéo avec son (après clic utilisateur)
  const handleStart = () => {
    setPhase(PHASES.VIDEO);
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.muted = false;
        videoRef.current.play();
      }
    }, 100);
  };

  // Video end handler - passage automatique au portail
  const handleVideoEnd = () => {
    setPhase(PHASES.PORTAL);
  };

  // Revoir le film depuis le portail -> retour à l'écran d'accueil
  const handleReplayVideo = () => {
    setPhase('START');
    setCanSkip(false);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
    }
  };

  // Fallback timer si la vidéo ne se charge pas
  useEffect(() => {
    if (phase === PHASES.VIDEO) {
      const fallbackTimer = setTimeout(() => {
        setPhase(PHASES.PORTAL);
      }, 50000);
      return () => clearTimeout(fallbackTimer);
    }
  }, [phase]);

  // Secret admin access - Ctrl+Shift+A only
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        setShowAdminPanel(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleSkip = () => {
    // Skip va directement au portail de choix (pas aux bulles)
    setPhase(PHASES.PORTAL);
  };

  const handleAdminLogin = async () => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: adminCredentials.email,
        password: adminCredentials.password
      });

      const { access_token, user } = response.data;
      
      if (user.role === 'admin') {
        localStorage.setItem('tkale_token', access_token);
        localStorage.setItem('tkale_user', JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        toast.success('Connexion admin réussie !');
        // Refresh la page pour charger le user correctement
        window.location.href = '/';
      } else {
        toast.error('Accès administrateur requis');
      }
    } catch (error) {
      toast.error('Identifiants incorrects');
    }
  };

  const handleClientChoice = async () => {
    // Redirect to login
    onComplete('client');
  };

  const handleVisitorChoice = async () => {
    // Redirect to free registration
    onComplete('visitor');
  };

     useEffect(() => {
    if (videoRef.current && videoLoaded) {
      const tracks = videoRef.current.textTracks;
      const userLang = navigator.language.split('-')[0];
      let activeTrack = null;

      for (let i = 0; i < tracks.length; i++) {
        tracks[i].mode = 'hidden';
        if (tracks[i].language === userLang) {
          activeTrack = tracks[i];
        }
      }

      if (!activeTrack && tracks.length > 0) {
        activeTrack = tracks[0];
      }

      if (activeTrack) {
        activeTrack.mode = 'hidden';
        activeTrack.addEventListener('cuechange', () => {
          const cues = activeTrack.activeCues;
          if (cues && cues.length > 0) {
            setCurrentSubtitle(cues[0].text);
          } else {
            setCurrentSubtitle('');
          }
        });
        // Forcer la première cue
        setTimeout(() => {
          const cues = activeTrack.activeCues;
          if (cues && cues.length > 0) {
            setCurrentSubtitle(cues[0].text);
          }
        }, 500);
      }
    }
  }, [videoLoaded]);

  return (
    <div className="fixed inset-0 z-50 bg-black">
      
      {/* PHASE 0: ÉCRAN DE DÉMARRAGE - Clic pour lancer avec son */}
      <AnimatePresence>
        {phase === 'START' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-center px-6"
            >
              {/* Le poulpe en bocal */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="mb-8"
              >
                <div 
                  className="mx-auto rounded-full overflow-hidden"
                  style={{
                    width: '250px',
                    height: '250px',
                    boxShadow: '0 0 60px rgba(59, 130, 246, 0.5)',
                    border: '3px solid rgba(255,255,255,0.3)',
                  }}
                >
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  >
                    <source src="/videos/poulpe-background.mp4" type="video/mp4" />
                  </video>
                </div>
              </motion.div>

              {/* Message d'accueil */}
              <motion.h1 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-6 whitespace-nowrap"
              >
                Bienvenue sur T-Kalé
              </motion.h1>
              
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1, duration: 0.5 }}
                className="text-sm sm:text-xl text-cyan-300 mb-10 max-w-lg mx-auto"
              >
                Si c'est bien ici que vous vouliez entrer...
              </motion.p>

              <motion.button
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.5 }}
                onClick={handleStart}
                className="px-12 py-4 bg-cyan-600 hover:bg-cyan-500 text-white text-xl font-semibold rounded-full transition-all transform hover:scale-105 shadow-lg shadow-cyan-500/30"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                🐙 Merci de cliquer ici
              </motion.button>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 0.5 }}
                className="text-slate-400 mt-6 text-sm"
              >
                🔊 Activez le son pour une expérience immersive
              </motion.p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PHASE 1: REALISTIC VIDEO */}
      <AnimatePresence>
        {phase === PHASES.VIDEO && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0"
          >
            {/* Vidéo avec SON */}
            <video
              ref={videoRef}
              crossOrigin="anonymous"
              
              playsInline
              muted={false}
              controls={false}
              className="absolute inset-0 w-full h-full object-cover z-10"
              poster={MEDIA_CONFIG.intro.poster}
              onLoadedData={() => {
                setVideoLoaded(true);
              }}
              onEnded={handleVideoEnd}
              onError={() => {
                setVideoLoaded(false);
                // Si pas de vidéo, aller direct au portail après 2s
                setTimeout(() => setPhase(PHASES.PORTAL), 2000);
              }}
            >
              <source 
  src={MEDIA_CONFIG.intro.video}
  type="video/mp4" 
/>
<track kind="subtitles" srcLang="fr" label="Français" src="/subtitles/fr.vtt" default />
<track kind="subtitles" srcLang="en" label="English" src="/subtitles/en.vtt" />
<track kind="subtitles" srcLang="es" label="Español" src="/subtitles/es.vtt" />
<track kind="subtitles" srcLang="pt" label="Português" src="/subtitles/pt.vtt" />
<track kind="subtitles" srcLang="de" label="Deutsch" src="/subtitles/de.vtt" />
<track kind="subtitles" srcLang="it" label="Italiano" src="/subtitles/it.vtt" />
<track kind="subtitles" srcLang="ja" label="日本語" src="/subtitles/ja.vtt" />
<track kind="subtitles" srcLang="zh" label="中文" src="/subtitles/zh.vtt" />
<track kind="subtitles" srcLang="ar" label="العربية" src="/subtitles/ar.vtt" />
<track kind="subtitles" srcLang="ru" label="Русский" src="/subtitles/ru.vtt" />
<track kind="subtitles" srcLang="hi" label="हिन्दी" src="/subtitles/hi.vtt" />
<track kind="subtitles" srcLang="ko" label="한국어" src="/subtitles/ko.vtt" />

            </video>
{currentSubtitle && (
  <div style={{
    position: 'absolute',
    bottom: '10%',
    width: '100%',
    textAlign: 'center',
    zIndex: 20,
    pointerEvents: 'none',
  }}>
    <span style={{
      color: 'white',
      fontSize: '1.1em',
      textShadow: '1px 1px 3px black, -1px -1px 3px black',
      fontFamily: 'inherit',
    }}>
      {currentSubtitle}
    </span>
  </div>
)}


            {/* Fond noir si vidéo ne charge pas */}
            {!videoLoaded && (
              <div className="absolute inset-0 bg-black z-0 flex items-center justify-center">
                <div className="text-white text-xl">Chargement...</div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* PHASE 2: CHOIX CLIENT/VISITEUR (directement après la vidéo) */}
      <AnimatePresence>
        {phase === PHASES.PORTAL && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 flex items-center justify-center"
            style={{
              background: 'radial-gradient(circle, rgba(10,40,80,0.95) 0%, rgba(0,20,40,1) 100%)'
            }}
          >
            <div className="relative z-10 max-w-4xl w-full px-8">
              <motion.h2
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                className="text-2xl sm:text-4xl font-bold text-center text-cyan-300 mb-4 sm:mb-12"
              >
                Bienvenue sur T-Kalé
              </motion.h2>

              {!showAdminPanel && (
                <div className="grid grid-cols-1 gap-3">
                  {/* Client Option */}
                  <motion.div
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(59, 130, 246, 0.6)' }}
                    onClick={handleClientChoice}
                    className="cursor-pointer h-full"
                  >
                    <Card className="bg-blue-900/50 border-blue-500 hover:border-blue-400 transition-all backdrop-blur-sm h-full">
                      <CardContent className="p-4 sm:p-8 text-center flex flex-col justify-between h-full min-h-[180px] sm:min-h-[320px]">
                        <div>
                          <User className="w-12 h-12 sm:w-20 sm:h-20 mx-auto mb-2 sm:mb-4 text-blue-400" />
                          <h3 className="text-2xl font-bold text-white mb-2">Client</h3>
                          <p className="text-blue-200 mb-4">
                            J'ai déjà un compte T-Kalé
                          </p>
                        </div>
                        <Button className="w-full bg-blue-600 hover:bg-blue-500">
                          Se connecter
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Visitor Option */}
                  <motion.div
                    initial={{ x: 100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(16, 185, 129, 0.6)' }}
                    onClick={handleVisitorChoice}
                    className="cursor-pointer h-full"
                  >
                    <Card className="bg-teal-900/50 border-teal-500 hover:border-teal-400 transition-all backdrop-blur-sm h-full">
                      <CardContent className="p-4 sm:p-8 text-center flex flex-col justify-between h-full min-h-[180px] sm:min-h-[320px]">
                        <div>
                          <Eye className="w-12 h-12 sm:w-20 sm:h-20 mx-auto mb-2 sm:mb-4 text-teal-400" />
                          <h3 className="text-2xl font-bold text-white mb-2">Visiteur</h3>
                          <p className="text-teal-200 mb-4">
                            Découvrir gratuitement T-Kalé
                          </p>
                          <div className="bg-teal-800/50 rounded-lg p-3 mb-4">
                            <p className="text-sm text-teal-300">
                              ✨ 10 crédits offerts
                            </p>
                          </div>
                        </div>
                        <Button className="w-full bg-teal-600 hover:bg-teal-500">
                          Explorer maintenant
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                </div>
              )}

              {/* Bouton Revoir l'intro - visible sur le portail */}
              {!showAdminPanel && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-center mt-8"
                >
                  <Button
                    onClick={handleReplayVideo}
                    variant="ghost"
                    className="text-cyan-400 hover:text-cyan-200 hover:bg-white/10"
                  >
                    🎬 Revoir l'intro
                  </Button>
                </motion.div>
              )}

              {/* Admin Panel (hidden by default) */}
              {showAdminPanel && (
                <motion.div
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Card className="bg-slate-900/90 border-purple-500 backdrop-blur-sm max-w-md mx-auto">
                    <CardContent className="p-8">
                      <div className="flex items-center justify-center mb-6">
                        <Shield className="w-12 h-12 text-purple-400 mr-3" />
                        <h3 className="text-2xl font-bold text-white">Accès Administrateur</h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="admin-email" className="text-white">Email</Label>
                          <Input
                            id="admin-email"
                            type="email"
                            value={adminCredentials.email}
                            onChange={(e) => setAdminCredentials(prev => ({ ...prev, email: e.target.value }))}
                            className="bg-slate-800 text-white border-purple-500"
                            placeholder="votre@email.com"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="admin-password" className="text-white">Mot de passe</Label>
                          <Input
                            id="admin-password"
                            type="password"
                            value={adminCredentials.password}
                            onChange={(e) => setAdminCredentials(prev => ({ ...prev, password: e.target.value }))}
                            className="bg-slate-800 text-white border-purple-500"
                            placeholder="••••••••"
                          />
                        </div>
                        
                        <Button
                          onClick={handleAdminLogin}
                          className="w-full bg-purple-600 hover:bg-purple-500"
                        >
                          Connexion sécurisée
                        </Button>
                        
                        <Button
                          onClick={() => setShowAdminPanel(false)}
                          variant="ghost"
                          className="w-full text-gray-400"
                        >
                          Annuler
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Skip Button */}
      <AnimatePresence>
        {canSkip && phase !== PHASES.PORTAL && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute top-8 right-8 z-50"
          >
            <Button
              onClick={handleSkip}
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Passer l'intro
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default RealisticIntro;
